import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormContentErrorModel, FormContentModel, FormContentValidationErrorModel } from '@libs/cms/models';
import { StepComponent } from '@libs/form/components';
import { ErrorHandlerService, FlashMessageService, SpinnerOverlayService } from '@libs/shared/common';
import { GroupValueType, SubmitItem } from '@libs/form/models';
import { AppError } from '@libs/shared/models';
import { CmsService } from '@libs/shared/services';
import { StatusCodes } from 'http-status-codes';
import { firstValueFrom } from 'rxjs';
import { ContentBaseComponent } from '../content-base.component';

@Component({
  selector: 'ui-form-content',
  templateUrl: './form-content.component.html',
  styleUrls: ['./form-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    StepComponent,
  ],
})
export class FormContentComponent extends ContentBaseComponent<FormContentModel> {
  private readonly cmsService: CmsService = inject(CmsService);
  private readonly spinnerService: SpinnerOverlayService = inject(SpinnerOverlayService);
  private readonly messageService: FlashMessageService = inject(FlashMessageService);
  private readonly errorHandler: ErrorHandlerService = inject(ErrorHandlerService);

  public readonly formGroup: FormGroup = new FormGroup({});

  public onSubmit(submitter: SubmitItem): void {
    this.formGroup.markAllAsTouched();
    this.formGroup.markAllAsDirty();

    if (!this.formGroup.valid) {
      return;
    }

    this.spinnerService.showLoader();

    void firstValueFrom(this.cmsService.submitAppForm(this.data(), this.formGroup.value as GroupValueType))
      .then(() => void this.handleSubmissionSuccess(submitter))
      .catch((error: AppError<FormContentErrorModel>) => void this.handleSubmissionError(error))
      .finally(() => void this.spinnerService.hideLoader());
  }

  private handleSubmissionSuccess(submitter: SubmitItem): void {
    if (!submitter.successMessage) {
      this.messageService.showMessage('successContentSubmitForm');

      return;
    }

    this.messageService.showCustomMessage({
      theme: 'success',
      message: submitter.successMessage,
      duration: 5000,
    });
  }

  private handleSubmissionError(error: AppError): void {
    if (error.status !== Number(StatusCodes.UNPROCESSABLE_ENTITY) || !Array.isArray(error.data)) {
      this.errorHandler.handleError(error);
      this.messageService.showMessage('errorContentSubmitForm');

      return;
    }

    const validationErrors: FormContentValidationErrorModel[] = error.data as FormContentValidationErrorModel[];

    validationErrors.forEach(validationError => {
      this.formGroup.get(validationError.fieldName)?.setErrors({
        custom: validationError.message,
      });
    });
  }
}
